import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Row,
  Spin,
  Upload,
  VertButton,
  VertContainer,
  VertTypography,
  message,
} from '@vertem-core/vertice-ds';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import redemptionsApiClient from 'Services/Apis/Spot/redemptionsClient';
import ModalCancel from './VertModalCancel';
import ModalApprove from './VertModalAprove';

import { getOnlyNumbers, removeSpecialCharacters } from 'Utils/format';
import SpotltmClient from 'Services/Apis/Spot/ltmApiService';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';
import generateUUID from 'Utils/generateUUID';

const SpotTerms = ({
  spot,
  products,
  isLoading,
  isPMO,
  isLead,
  quote,
  isErrorsForm,
}) => {
  const userProfile = useSelector((state) => state.oidc.user.profile);
  const userInfos = useSelector(({ oidc: data }) => data);

  const { addressBilling, addressShipping } = useSelector(
    (state) => state.ApproveQuotation
  );

  const {
    user: { profile, access_token },
  } = userInfos;
  const username = profile.username || profile['custom:login'];

  const config = {
    campaignId: userProfile['custom:campaign_id'],
    catalogId: userProfile['custom:catalog_id'],
    participantId: userProfile['custom:participant_id'],
    Authorization: `Bearer ${access_token}`,
    username: username,
  };

  useEffect(() => {
    getParticipantData();
  }, []);

  const {callApi } = usePipeContext();
  const navigate = useNavigate();
  const params = useParams();
  const [isVisibleModalCancel, setIsVisibleModalCancel] = useState(false);
  const [isVisibleModalAprovePMO, setIsVisibleModalAprovePMO] = useState(false);
  const [isLoadingAction, setLoadingAction] = useState(false);
  const [participantData, setParticipantData] = useState(null);

  const callModal = (type) => {
    if (type === 'cancel') {
      setIsVisibleModalCancel(true);
    } else if (type === 'approve') {
      setIsVisibleModalAprovePMO(true);
    }
  };

  const cancelQuote = async (values) => {
    const { description } = values;

    setIsVisibleModalCancel(false);
    setLoadingAction(true);
    const data = {
      redemptionId: params.id,
      description,
    };

    redemptionsApiClient
      .put(`/Redemption/Refuse`, data, {
        headers: config,
      })
      .then((res) => {
        setLoadingAction(false);
        navigate('/list-redemptions', {
          state: {
            spotCanceled: true,
            spotId: params.id,
          },
        });
      })
      .catch((err) => {
        console.log('Solicitation Error === ', err);
        message.error(
          'Não foi possível cancelar esta solicitação, valide as informações e tente novamente.'
        );
        setLoadingAction(false);
      });
  };

  const getParticipantData = async () => {
    await SpotltmClient.get(`/cloudloyalty/v1/participants/me`, {
      headers: config,
    })
      .then((res) => {
        setParticipantData(res.data);

        return res.data;
      })
      .catch((err) => {
        console.log('getParticipantData err ::: ', err);
      });
  };

  const updateParticipantAddress = async (person, address) => {
    let result = { error: true };
    const mappedEmails =
      person.emails && person.emails.length > 0
        ? person.emails.map((emailObj) => ({
            email: emailObj.email.toLowerCase(),
            type: emailObj.type,
          }))
        : [];
    const mappedPhones =
      person.phones && person.phones.length > 0
        ? person.phones.map((phones) => ({
            areaCode: getOnlyNumbers(phones.areaCode),
            number: getOnlyNumbers(phones.number),
            type: phones.type,
          }))
        : [];
    const data = {
      id: person.id,
      profileId: person.profileId,
      name: removeSpecialCharacters(person.name),
      companyName:
        person.personType === 'INDIVIDUAL'
          ? null
          : removeSpecialCharacters(person.name),
      username: person.username,
      documentNumber: getOnlyNumbers(person.documentNumber),
      partnerCpf: person.personType === 'INDIVIDUAL' ? null : person.partnerCpf,
      rg: person.rg,
      ie: person.personType === 'INDIVIDUAL' ? null : person.ie,
      birthDate: person.birthDate,
      persontypeId: person.persontypeId,
      persontype: person.persontype,
      genderTypeId: person.genderTypeId ? person.genderTypeId : 3,
      genderType: person.genderType ? person.genderType : 'UNINFORMED',
      maritalStatusId: person.maritalStatusId ? person.maritalStatusId : 6,
      maritalStatus: person.maritalStatus ? person.maritalStatus : 'UNINFORMED',
      statusId: person.statusId,
      status: person.status,
      emails: mappedEmails,
      phones: mappedPhones,
      address: {
        city: address.city,
        complement:
          address.complement && address.complement.length > 0
            ? address.complement
            : 'Não informado',
        district: address.neighborhood,
        number:
          address.number && address.number.length > 0 ? address.number : '0',
        reference:
          address.reference && address.reference.length > 0
            ? address.reference
            : 'Nenhuma',
        state: address.state,
        street: address.address,
        zipCode: address.zipCode.replace('-', '').replace('.', ''),
        status: 1,
        statusId: 1,
      },
    };

    await SpotltmClient.put('/cloudloyalty/v1/participants/me', data, {
      headers: config,
    })
      .then((res) => {
        result = { error: false };
      })
      .catch((err) => {
        console.log('saveNewAddress err ::: ', err);
      });
    return result;
  };

  const approveQuote = async (values) => {
    let fmtShippingAddress = {...addressShipping};
    let fmtBillingAddress = null;

    if (addressShipping.number == "S/N") fmtShippingAddress.number = '0';

    if (addressBilling !== null) {
      fmtBillingAddress = {...addressBilling};

      if (addressBilling.number == "S/N") fmtBillingAddress.number = '0';
    }

    let productsIds = [];
    const { description } = values;
    setLoadingAction(true);
    setIsVisibleModalAprovePMO(false);
    await updateParticipantAddress(participantData, fmtShippingAddress);

    products.map((product) => {
      return productsIds.push(product.id);
    });

    const _data = {
      redemptionId: spot.id,
      description,
      status: 6,
      termUrl: null,
      quoteId: quote.id,
      productsIds,
      deliveryAddress: fmtShippingAddress,
      billingAddress:
        addressBilling === null ? fmtShippingAddress : fmtBillingAddress,
    };

    await redemptionsApiClient
      .put(`/Redemption/Approve/Quote`, _data, {
        headers: config,
      })
      .then((res) => {
        callApi({
          symbol: 'LOAD_BALANCE',
          alias: generateUUID(),
        })



        if (spot.type === 4 && res.data.failed.length > 0) {
          message.error(
            'Um ou mais produtos não foram aprovados. Entre em contato com o responsável.'
          );
          setLoadingAction(false);

          setIsVisibleModalAprovePMO(false);

          return false;
        }
        message.success('Solicitação de cotação aprovada com sucesso.');

        navigate('/list-redemptions', {
          state: {
            spotApproved: true,
            spotId: spot.id,
          },
        });
      })
      .catch((err) => {
        console.log('approveQuote Error === ', err);
        setIsVisibleModalAprovePMO(false);
        if (
          err.response.data.code === 400001 ||
          err.response.data.code === '400001'
        ) {
          message.error('O saldo atual é inferior ao valor do produto.');
        } else {
          message.error(
            'Não foi possível aprovar a solicitação, valide as informações e tente novamente.'
          );
        }
      });

    setLoadingAction(false);
  };

  return (
    <>
      {products && (
        <>
          <Row justify="center" gutter={[16, 16]} verticalSpacing="regular">
            {isPMO && (
              <VertTypography.Paragraph className="title-terms-aprovation">
                Para dar continuidade, essa solicitação precisa de sua
                aprovação. O que deseja fazer?
              </VertTypography.Paragraph>
            )}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button
                type="default"
                danger
                loading={isLoading || isLoadingAction}
                onClick={() => callModal('cancel')}
                className="button-view-redemption-danger"
              >
                Cancelar Solicitação
              </Button>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <VertButton
                type="highlight"
                loading={isLoading || isLoadingAction}
                onClick={() => {
                  if (addressShipping !== null) {
                    callModal('approve');
                    return;
                  }
                }}
                className="button-view-redemption-danger"
                htmlType="submit"
                form="formApproveSpot"
                disabled={isErrorsForm}
              >
                Aprovar Solicitação
              </VertButton>
            </Col>
          </Row>
        </>
      )}

      <Row
        justify="center"
        className="row-button"
        verticalSpacing="regular"
        gutter={[16, 16]}
      >
        <Col xs={24} sm={12} md={8} lg={6}>
          <VertButton
            type="default"
            className="button-view-redemption"
            loading={isLoading || isLoadingAction}
            onClick={() => navigate('/list-redemptions')}
          >
            Ver Solicitações
          </VertButton>
        </Col>

        <ModalCancel
          isVisible={isVisibleModalCancel}
          setIsVisible={setIsVisibleModalCancel}
          spot={spot}
          isPMO={isPMO}
          isLead={isLead}
          submitCancel={cancelQuote}
        />
        <ModalApprove
          isPMO={isPMO}
          isLead={isLead}
          isVisible={isVisibleModalAprovePMO}
          setIsVisible={setIsVisibleModalAprovePMO}
          submitApprove={approveQuote}
        />
      </Row>
    </>
  );
};

export default SpotTerms;

SpotTerms.propTypes = {
  spot: PropTypes.object.isRequired,
};
